import * as React from 'react';
import { useState } from 'react';
import { Avatar, Box, Stack, Typography } from '@mui/material';
import { Calendar, CheckCircle, Mail, Phone, PhoneCall, ShoppingBag, Star, Zap } from 'lucide-react';
import { useAgent } from '../../../../../shared';
import { environment } from '../../../../../../environments/environment';
import { ScheduleACallButton } from '@coverright/features';
import { medicareAiTheme } from '@coverright/ui/themes';

// ─── Component ────────────────────────────────────────────────────────────────

export const AgentCard = () => {
  const [booked, setBooked] = useState(false);
  const { agent } = useAgent();

  const fullName   = agent ? `${agent.firstName} ${agent.lastName}` : '';
  const initials   = agent ? `${agent.firstName[0]}${agent.lastName[0]}`.toUpperCase() : '';
  const avatarSrc  = agent?.picUrl ? environment.cdnUrl + agent.picUrl : undefined;
  const bookingUrl = agent?.enrollmentMeetingUrl ?? agent?.consultationUrl ?? '#';

  return (
    <Box sx={{
      width: { xs: '100%', md: 260 }, flexShrink: 0,
      border: '1px solid #e5e7eb', borderRadius: 1.5, overflow: 'hidden',
      boxShadow: '0 20px 40px rgba(209,213,219,0.5)',
      bgcolor: 'white', display: 'flex', flexDirection: 'column',
    }}>
      {/* Header */}
      <Box sx={{
        bgcolor: '#f8faff', borderBottom: '1px solid #f3f4f6',
        p: 2, borderRadius: '16px 16px 0 0',
      }}>
        {!booked ? (
          <>
            <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#111827' }}>Book a time!</Typography>
            <Typography sx={{ fontSize: 13, color: '#4b5563', lineHeight: 1.4 }}>
              Speak with your dedicated licensed agent.
            </Typography>
          </>
        ) : (
          <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#111827' }}>Consultation booked</Typography>
        )}
      </Box>

      {/* Body */}
      <Box sx={{ p: 2 }}>
        {/* Avatar + name */}
        <Stack direction="row" alignItems="center" gap={1.5} sx={{ mb: 2.5 }}>
          <Box sx={{ position: 'relative', flexShrink: 0 }}>
            <Avatar
              src={avatarSrc}
              alt={fullName}
              sx={{
                width: 48, height: 48,
                border: '1px solid #e5e7eb',
                boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                bgcolor: '#0A6C84', color: 'white',
                fontWeight: 700, fontSize: 16,
              }}
            >
              {initials}
            </Avatar>
            <Box sx={{
              position: 'absolute', bottom: 0, right: 0,
              width: 14, height: 14, bgcolor: '#10b981',
              borderRadius: '50%', border: '2px solid white', zIndex: 1,
            }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>{fullName}</Typography>
            <Typography sx={{ fontSize: 11, color: '#6b7280', mb: 0.25 }}>Licensed Agent</Typography>
            <Stack direction="row" gap={0.25}>
              {[1, 2, 3, 4, 5].map(i => (
                <Star key={i} size={12} fill="#10b981" color="#10b981" />
              ))}
            </Stack>
          </Box>
        </Stack>

        {/* CTAs */}
        <Stack gap={1.5} sx={{ mb: 2.5 }}>
          {!booked ? (
            <ScheduleACallButton
              variant="outlined"
              color="primary"
              size="medium"
              fullWidth
              startIcon={<Calendar size={16} style={{ flexShrink: 0 }} />}
              sx={{
                bgcolor: '#009689', color: '#fff',
                fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap',
                height: 40,
                transition: 'background 150ms', '&:hover': { bgcolor: '#008276' },
              }}
              calendlyProps={{
                agent,
                theme: medicareAiTheme,
                onEventScheduled: () => setBooked(true),
                sx: {zIndex: 1600}
              }}
            >
              Schedule an appointment
            </ScheduleACallButton>
          ) : (
            <Stack gap={1.5}>
              <Box sx={{
                width: '100%', bgcolor: '#ecfdf5',
                border: '1px solid rgba(16,185,129,0.3)',
                borderRadius: 2, py: 1.25,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
              }}>
                <CheckCircle size={20} color="#059669" />
                <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#059669' }}>Call Scheduled</Typography>
              </Box>
              <Box sx={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                bgcolor: '#f9fafb', border: '1px solid #f3f4f6',
                borderRadius: 2, py: 1.25, gap: 0.5,
              }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>
                  Tomorrow, 10:00 AM EST
                </Typography>
                <Box
                  component="a"
                  href={bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontSize: 11, color: '#3b82f6', fontWeight: 500, textDecoration: 'underline', cursor: 'pointer' }}
                >
                  Need to reschedule?
                </Box>
              </Box>
            </Stack>
          )}

          {/* Contact links */}
          <Stack gap={1} sx={{ borderTop: '1px solid #f3f4f6', pt: 1.5 }}>
            {agent?.phone && (
              <Box
                component="a"
                href={`tel:${agent.phone.replace(/\D/g, '')}`}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.25,
                  fontSize: 13, fontWeight: 600, color: '#374151',
                  textDecoration: 'none',
                  '&:hover': { color: '#009689' }, transition: 'color 150ms',
                }}
              >
                <Box sx={{ width: 24, height: 24, borderRadius: 1, bgcolor: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Phone size={12} color="#6b7280" />
                </Box>
                {agent.phone}
              </Box>
            )}
            {agent?.email && (
              <Box
                component="a"
                href={`mailto:${agent.email}`}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.25,
                  fontSize: 13, fontWeight: 500, color: '#4b5563',
                  textDecoration: 'none',
                  '&:hover': { color: '#009689' }, transition: 'color 150ms',
                }}
              >
                <Box sx={{ width: 24, height: 24, borderRadius: 1, bgcolor: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Mail size={12} color="#6b7280" />
                </Box>
                {agent.email}
              </Box>
            )}
          </Stack>
        </Stack>

        {/* Why call box */}
        <Box sx={{ bgcolor: '#fcfaf5', border: '1px solid #f5ebd6', borderRadius: 2, p: 1.75 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#111827', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>
            Why should you call us?
          </Typography>
          {[
            { icon: <ShoppingBag size={14} color="#9ca3af" />, text: "It's 100% free" },
            { icon: <PhoneCall   size={14} color="#9ca3af" />, text: 'You want expert advice' },
            { icon: <Zap         size={14} color="#9ca3af" />, text: 'Ready to enroll' },
          ].map(({ icon, text }) => (
            <Stack key={text} direction="row" alignItems="center" gap={1.25}>
              <Box sx={{ flexShrink: 0 }}>{icon}</Box>
              <Typography sx={{ fontSize: 12, color: '#4b5563', lineHeight: 1.4 }}>{text}</Typography>
            </Stack>
          ))}
        </Box>
      </Box>
    </Box>
  );
};
